import { AgencySignupPayload, AgencySignupSource, Industry, MdtPurchaseWindow } from '@clipr/lib';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiService } from '../../api/apiService';
import { AgencyTerminology, agencyTerminology } from '../../components/agency/agencyTerminology';
import { resolveAgencySignupError } from './agencySignupErrors';
import {
  AGENCY_SIGNUP_DRAFT_KEY,
  AgencyOfficerFormRow,
  AgencySignupFormModel,
  createAgencySignupFormModel,
  createEmptyOfficerRow,
  getRosterLimits,
  isOfficerRowComplete,
  isOfficerRowEmpty,
  MAX_PILOT_OFFICERS,
} from './agencySignupSchema';
import { parseAgencyRosterCsv } from './agencyCsv';

type AgencySignupStore = {
  apiService: Pick<ApiService, 'saveAgencySignupDraft' | 'resumeAgencySignupDraft' | 'submitAgencySignup'>;
};

type DraftPayload = Omit<AgencySignupPayload, 'sponsorPassword' | 'swornOfficersCount' | 'officers'> & {
  swornOfficersCount: number;
  officers: AgencyOfficerFormRow[];
};

/** `source` is a route fact, not a form field; a resumed draft must not overwrite the live one. */
const DRAFT_FIELDS_NOT_RESTORED = ['source'] as const;

const SAVE_DRAFT_DELAY_MS = 3000;

export class AgencySignupPageState {

  constructor(readonly store: AgencySignupStore) {
    makeObservable(this);
  }

  /**
   * Landing page this form is being filled on. Drives the visible wording and, on submit, the
   * industry the new Organization is created with -- see `terms`.
   */
  @observable source: AgencySignupSource | null = null;
  @observable form: AgencySignupFormModel = createAgencySignupFormModel();
  @observable isSavingDraft = false;
  @observable isSubmitting = false;
  @observable isResuming = false;
  @observable saveStatus: string | null = null;
  @observable error: string | null = null;
  @observable rosterCsvError: string | null = null;
  @observable rosterCsvStatus: string | null = null;
  @observable submitResult: { orgId: string; sponsorUserId: string; agencyTemplateId: string } | null = null;

  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * The form's visible wording. /orgsignup creates an Other Services organization, so it reads the
   * generic copy the agency consoles already use for a non-Public-Safety industry; every other
   * source creates a Public Safety agency and keeps the officer/agency wording.
   */
  @computed
  get terms(): AgencyTerminology {
    return agencyTerminology(this.source === AgencySignupSource.Org ? Industry.OtherServices : null);
  }

  /** True on /orgsignup, where the MDT reseller program does not apply. */
  @computed
  get isOrgSignup(): boolean {
    return this.source === AgencySignupSource.Org;
  }

  @computed
  get swornOfficerCount(): number {
    return Number(this.form.swornOfficersCount) || 0;
  }

  @computed
  get agencyDomain(): string {
    return this.form.sponsorEmail.trim().toLowerCase().split('@')[1] ?? '';
  }

  @computed
  get pilotRosterLimit(): number {
    return getRosterLimits(this.swornOfficerCount).pilotRows;
  }

  @computed
  get isAccountValid(): boolean {
    return Boolean(
      this.form.sponsorFirstName.trim() &&
      this.form.sponsorLastName.trim() &&
      this.form.sponsorTitle.trim() &&
      this.form.sponsorEmail.trim() &&
      this.form.sponsorPassword.trim() &&
      this.form.agencyName.trim() &&
      this.agencyDomain &&
      this.swornOfficerCount > 0);
  }

  /**
   * The rows that actually go to the server. Generating a roster creates one row per declared head,
   * and a sponsor who wants to start the pilot with five of thirty should not have to fill in
   * twenty-five people first -- so untouched rows are dropped rather than blocking activation.
   */
  @computed
  get completeOfficers(): AgencyOfficerFormRow[] {
    return this.form.officers.filter(isOfficerRowComplete);
  }

  /**
   * Rows someone started and left unfinished, as 1-based numbers matching the table.
   *
   * These block submission instead of being dropped with the empty ones. Dropping them would throw
   * away a name the sponsor typed and silently leave that person out of the pilot.
   */
  @computed
  get partialOfficerRowNumbers(): number[] {
    return this.form.officers
      .map((row, index) => (isOfficerRowEmpty(row) || isOfficerRowComplete(row) ? 0 : index + 1))
      .filter((rowNumber) => rowNumber > 0);
  }

  @computed
  get areRequiredRowsComplete(): boolean {
    return this.completeOfficers.length > 0 && this.partialOfficerRowNumbers.length === 0;
  }

  /** Why the submit button is disabled, when the reason is the roster. Null means nothing to say. */
  @computed
  get rosterBlockingMessage(): string | null {
    const partial = this.partialOfficerRowNumbers;
    if (partial.length > 0) {
      const rows = partial.length === 1 ? 'Row' : 'Rows';
      return `${rows} ${partial.join(', ')} ${partial.length === 1 ? 'is' : 'are'} incomplete. `
        + `Fill every field or clear the row -- empty rows are left out of the pilot.`;
    }

    if (this.form.officers.length > 0 && this.completeOfficers.length === 0)
      return `Fill in at least one ${this.terms.officer} to start the pilot.`;

    return null;
  }

  @computed
  get canSubmit(): boolean {
    return this.isAccountValid &&
      this.areRequiredRowsComplete &&
      this.form.acceptedTerms &&
      !this.isSubmitting;
  }

  @action
  setSource(source?: AgencySignupSource) {
    this.source = source ?? null;
  }

  @action
  mounted(source?: AgencySignupSource) {
    this.setSource(source);
    // The state is a store singleton shared by every signup route, so a submission finished on one
    // of them would otherwise greet the next route with its own success panel and no form.
    this.submitResult = null;
    this.error = null;
    this.hydrateFromLocalStorage();
  }

  @action
  unmounted() {
    this.clearSaveTimer();
  }

  @action
  resetForm() {
    this.clearSaveTimer();
    this.form = createAgencySignupFormModel();
    this.error = null;
    this.submitResult = null;
    this.saveStatus = null;
    localStorage.removeItem(AGENCY_SIGNUP_DRAFT_KEY);
  }

  @action
  updateField<TKey extends keyof AgencySignupFormModel>(key: TKey, value: AgencySignupFormModel[TKey]) {
    this.form[key] = value;
    this.saveDraftDebounced();
  }

  @action
  updateOfficer(index: number, key: keyof AgencyOfficerFormRow, value: string) {
    const row = this.form.officers[index];
    if (!row)
      return;

    row[key] = isEmailKey(key) && value.endsWith('@') && !row[key].includes('@') ?
      value + this.agencyDomain :
      value;
    this.saveDraftDebounced();
  }

  @action
  generateRoster() {
    this.form.officers = resizeRows(this.form.officers, this.pilotRosterLimit);
  }

  @action
  importRosterCsv(data: string[][]) {
    const result = parseAgencyRosterCsv(data, this.terms);
    this.rosterCsvError = result.errors.length > 0 ?
      result.errors.slice(0, 8).join('\n') :
      null;

    if (result.rows.length === 0) {
      this.rosterCsvStatus = null;
      if (!this.rosterCsvError)
        this.rosterCsvError = `No ${this.terms.officer} rows were found in the CSV.`;
      return;
    }

    const officers = result.rows;
    if (!this.validateRosterCsvImport(officers.length))
      return;

    this.form.officers = officers;
    this.form.swornOfficersCount = String(Math.max(
      this.swornOfficerCount,
      this.form.officers.length,
    ));
    this.rosterCsvStatus =
      `${officers.length} ${this.terms.officer} ${getRosterRowLabel(officers.length)} imported.`;
    this.saveDraftDebounced();
  }

  private validateRosterCsvImport(rowCount: number): boolean {
    if (rowCount > MAX_PILOT_OFFICERS) {
      this.rosterCsvStatus = null;
      this.rosterCsvError = `CSV can include up to ${MAX_PILOT_OFFICERS} ${this.terms.officers}.`;
      return false;
    }

    return true;
  }

  @action
  saveDraftDebounced() {
    this.clearSaveTimer();
    this.saveStatus = 'Draft pending';
    this.persistLocalDraft();
    this.saveTimer = setTimeout(() => {
      void this.saveDraft();
    }, SAVE_DRAFT_DELAY_MS);
  }

  @action
  async saveDraft() {
    if (!this.form.sponsorEmail.trim())
      return;

    this.clearSaveTimer();
    this.isSavingDraft = true;

    const [res, err] = await this.store.apiService.saveAgencySignupDraft({
      email: this.form.sponsorEmail.trim(),
      payload: JSON.stringify(this.toApiPayload('draft')),
    });

    runInAction(() => {
      this.isSavingDraft = false;
      if (err) {
        this.saveStatus = 'Draft save failed — your progress is kept locally';
        return;
      }

      this.saveStatus = res?.saveAgencySignupDraft.updatedAt ?
        `Draft saved ${new Date(res.saveAgencySignupDraft.updatedAt).toLocaleTimeString()}` :
        'Draft saved';
    });
  }

  @action
  async resumeFromQuery(search: string) {
    const query = new URLSearchParams(search);
    const draftId = query.get('draft');
    const token = query.get('token');

    if (!draftId || !token) {
      this.error = 'Resume link is missing draft or token.';
      return;
    }

    this.isResuming = true;
    this.error = null;

    const [res, err] = await this.store.apiService.resumeAgencySignupDraft({ draftId, token });

    runInAction(() => {
      this.isResuming = false;
      if (err) {
        this.error = resolveAgencySignupError(err, this.terms);
        return;
      }

      this.hydrateFromPayload(res?.resumeAgencySignupDraft.payload, { trustSource: true });
      this.saveStatus = 'Draft resumed';
    });
  }

  @action
  async submit(source?: AgencySignupSource) {
    if (!this.canSubmit)
      return;

    this.isSubmitting = true;
    this.error = null;

    const [res, err] = await this.store.apiService.submitAgencySignup({
      input: this.toApiPayload('submit', source),
    });

    runInAction(() => {
      this.isSubmitting = false;
      if (err) {
        this.error = resolveAgencySignupError(err, this.terms);
        return;
      }

      this.submitResult = res?.submitAgencySignup ?? null;
      this.saveStatus = null;
      localStorage.removeItem(AGENCY_SIGNUP_DRAFT_KEY);
    });
  }

  toApiPayload(mode: 'draft'): DraftPayload;
  toApiPayload(mode: 'submit', source?: AgencySignupSource): AgencySignupPayload;
  toApiPayload(mode: 'draft' | 'submit', source?: AgencySignupSource): DraftPayload | AgencySignupPayload {
    const base = {
      sponsorFirstName: this.form.sponsorFirstName.trim(),
      sponsorLastName: this.form.sponsorLastName.trim(),
      sponsorTitle: this.form.sponsorTitle.trim(),
      sponsorEmail: this.form.sponsorEmail.trim(),
      sponsorPhone: this.form.sponsorPhone.trim() || undefined,
      agencyName: this.form.agencyName.trim(),
      agencyDomain: this.agencyDomain,
      swornOfficersCount: this.swornOfficerCount,
      officers: this.form.officers.map(normalizeOfficerRow),
      ...this.programFields(),
      acceptedTerms: this.form.acceptedTerms,
      // Carried on the draft too, not just the submit: it is what tells the server which signup
      // form to point the resume email at, and a draft is the only thing that email knows about.
      ...(this.source ? { source: this.source } : {}),
    };

    if (mode === 'draft')
      return base;

    return {
      ...base,
      // The draft keeps every row so a half-typed one survives a reload; the submit sends only the
      // rows that are actually filled, which is the only shape the server accepts.
      officers: this.completeOfficers.map(normalizeOfficerRow),
      sponsorPassword: this.form.sponsorPassword,
      ...(source ? { source } : {}),
    };
  }

  /**
   * `trustSource` belongs to the resume routes only.
   *
   * A draft is stored per sponsor email, so saving on one signup form overwrites the draft the
   * other one made -- and the resume email keeps the path of whichever save created it. Opening a
   * stale link therefore loads the newer draft on the older form. On a resume the draft is the
   * truth and the link is a guess; on the live form the route is the truth and a restored payload
   * must not change which form is being filled in.
   */
  @action
  hydrateFromPayload(payload: unknown, { trustSource = false } = {}) {
    const draftPayload = parseDraftPayload(payload);
    if (!isDraftRecord(draftPayload))
      return;

    if (trustSource)
      this.setSource(readSource(draftPayload.source));

    const next = createAgencySignupFormModel();
    const {
      officers = [],
      minOfficers = [],
      maxOfficers = [],
      ...draftFields
    } = draftPayload;
    for (const field of DRAFT_FIELDS_NOT_RESTORED)
      delete (draftFields as Record<string, unknown>)[field];
    Object.assign(next, draftFields);
    next.sponsorPassword = '';
    next.swornOfficersCount = String(draftPayload.swornOfficersCount || '');
    next.officers = [
      ...officers,
      ...minOfficers,
      ...maxOfficers,
    ].slice(0, MAX_PILOT_OFFICERS).map(mergeOfficerRow);
    this.form = next;
    this.persistLocalDraft();
  }

  /**
   * Program answers are Public Safety's MDT reseller questions. /orgsignup renders none of them, so
   * submitting whatever a shared draft left in the model would persist answers on the organization
   * -- and forward them to the CRM -- that nobody was ever shown, let alone asked.
   */
  private programFields() {
    if (this.isOrgSignup) {
      return {
        reseller: '',
        mdtPurchaseWindow: MdtPurchaseWindow.NotSure,
        mdtManufacturer: '',
        mdtModel: '',
        bwcPlatform: '',
        rmsPlatform: '',
      };
    }

    return {
      reseller: this.form.reseller.trim(),
      resellerPocName: this.form.resellerPocName.trim() || undefined,
      resellerPocEmail: this.form.resellerPocEmail.trim() || undefined,
      mdtPurchaseWindow: this.form.mdtPurchaseWindow || MdtPurchaseWindow.NotSure,
      mdtManufacturer: this.form.mdtManufacturer.trim(),
      mdtModel: this.form.mdtModel.trim(),
      bwcPlatform: this.form.bwcPlatform.trim(),
      rmsPlatform: this.form.rmsPlatform.trim(),
    };
  }

  private hydrateFromLocalStorage() {
    const raw = localStorage.getItem(AGENCY_SIGNUP_DRAFT_KEY);
    if (!raw)
      return;

    try {
      this.hydrateFromPayload(JSON.parse(raw));
    } catch {
      localStorage.removeItem(AGENCY_SIGNUP_DRAFT_KEY);
    }
  }

  private persistLocalDraft() {
    localStorage.setItem(AGENCY_SIGNUP_DRAFT_KEY, JSON.stringify(this.toApiPayload('draft')));
  }

  private clearSaveTimer() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }
}

/** Stored drafts are client JSON; anything that is not a known source reads as the default form. */
function readSource(value: unknown): AgencySignupSource | undefined {
  return Object.values(AgencySignupSource).includes(value as AgencySignupSource) ?
    value as AgencySignupSource :
    undefined;
}

function resizeRows(rows: AgencyOfficerFormRow[], size: number): AgencyOfficerFormRow[] {
  return Array.from({ length: size }, (_item, index) => rows[index] ?? createEmptyOfficerRow());
}

function getRosterRowLabel(count: number): string {
  return `row${count === 1 ? '' : 's'}`;
}

function isEmailKey(key: keyof AgencyOfficerFormRow): boolean {
  return key === 'email' || key === 'supervisorEmail';
}

function normalizeOfficerRow(row: AgencyOfficerFormRow): AgencyOfficerFormRow {
  return {
    firstName: row.firstName.trim(),
    lastName: row.lastName.trim(),
    title: row.title.trim(),
    email: row.email.trim(),
    supervisorTitle: row.supervisorTitle.trim(),
    supervisorName: row.supervisorName.trim(),
    supervisorEmail: row.supervisorEmail.trim(),
  };
}

function mergeOfficerRow(row: Partial<AgencyOfficerFormRow>): AgencyOfficerFormRow {
  return {
    ...createEmptyOfficerRow(),
    ...row,
  };
}

function parseDraftPayload(payload: unknown): unknown {
  if (typeof payload !== 'string')
    return payload;

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isDraftRecord(value: unknown): value is Partial<AgencySignupFormModel> & {
  source?: unknown;
  swornOfficersCount?: number | string;
  officers?: Partial<AgencyOfficerFormRow>[];
  minOfficers?: Partial<AgencyOfficerFormRow>[];
  maxOfficers?: Partial<AgencyOfficerFormRow>[];
} {
  return typeof value === 'object' && value !== null;
}
